<template>
  <div identity="c-card-options-wrap">
    <cvt-collapse
      :accordion="true"
      :theme="mode === 'LIGHT' ? 'light' : 'dark'"
      :prepend-chevron="prependChevron"
    >
      <div v-if="showOption('desktop')" style="padding: 16px">
        <template-options :mode="mode" :is-card-element="isCardElement" />
      </div>

      <!-- Implement Icon options -->
      <cvt-collapse-item
        title="Button"
        name="Button"
        :special-style="isSpecialStyle"
      >
        <div class="row">
          <div class="col">
            <strong>Enable Button</strong>
          </div>
          <div class="col-3">
            <cvt-switch
              size="xs"
              color="active"
              type="circular"
              description-type="default"
              :initial-state="enableButton"
              :mode="mode"
              @switchToggle="() => onSwitchToggle('enableButton')"
            />
          </div>
        </div>
        <div
          v-if="isCardElement"
          class="row mt-4 d-flex justify-content-between"
        >
          <div class="col-12">
            <alignment
              :component-key="`template/buttonSelfPosition`"
              alignment-label="Positioning"
              :mode="mode"
            />
          </div>
        </div>
      </cvt-collapse-item>

      <!-- Implement Icon options -->
      <cvt-collapse-item
        v-if="showOption('desktop')"
        title="Icon"
        name="Icon"
        :special-style="isSpecialStyle"
      >
        <!--        <div class="d-flex my-2">-->
        <!--          <strong class="mr-2 mb-3">Generative AI</strong>-->
        <!--          <GenAIOptions v-if="genAiEnabled" :mode="mode" />-->
        <!--        </div>-->

        <icon-image-options :mode="mode" />
      </cvt-collapse-item>

      <!-- Headline Text -->
      <cvt-collapse-item
        :title="$t('elements.property.card.element.options.headline')"
        name="headline"
      >
        <div class="d-flex my-2 flex-column">
          <strong class="mr-2 mb-3">Generative AI</strong>
          <GenAIOptions
            v-if="genAiEnabled"
            :mode="mode"
            path-prefix="template/headline"
            selector-value-key="headline/text"
          />
        </div>
        <text-options
          :content="headLineHTML"
          :config="headlineConfig"
          :show-text-editor="!isCardElement"
          :update-key="'headline/enable'"
          :is-enabled="enableHeadline"
          :label="$t('elements.property.card.element.options.edit.headline')"
          @save="(val) => selectAction(val, 'headLineHTML')"
        />
      </cvt-collapse-item>

      <!-- Text -->
      <cvt-collapse-item
        :title="$t('editor.left_nav.element.text')"
        name="text"
      >
        <div class="d-flex my-2 flex-column">
          <strong class="mr-2 mb-3">Generative AI</strong>
          <GenAIOptions
            v-if="genAiEnabled"
            :mode="mode"
            path-prefix="template/subHeadline"
            selector-value-key="subHeadline/text"
          />
        </div>
        <text-options
          :content="textHTML"
          :config="textConfig"
          :update-key="'subHeadline/enable'"
          :show-text-editor="!isCardElement"
          :is-enabled="enableText"
          :label="$t('editor.section.grid_item.edit.btn_text')"
          @save="(val) => selectAction(val, 'textHTML')"
        />
      </cvt-collapse-item>

      <!-- Implement Style options -->
      <cvt-collapse-item
        :title="$t('elements.properties.local.style.label')"
        name="style"
      >
        <style-option :mode="mode" />
        <border-style-options
          :mode="mode"
          :border-style="selected.borderStyle || 'none'"
          :border-color="selected.borderColor || null"
          :border-width="selected.borderWidth || 0"
          :border-radius="selected.borderRadius || 0"
          :border-position="selected.borderPosition || {}"
          :show-border-position="true"
          :update-prop-function="selected.updateProp"
        />
      </cvt-collapse-item>

      <!-- Implement Background options -->
      <cvt-collapse-item
        :title="$t('elements.properties.background.color')"
        name="background"
      >
        <background :mode="mode" />
      </cvt-collapse-item>
    </cvt-collapse>
  </div>
</template>

<script>
import * as _ from 'lodash'
import { mapGetters, mapState } from 'vuex'
import * as getters from '@/js/store/modules/getters'
import { rgbToHex } from '@/js/lib/color-utils'
import { FROALA_FONT_SIZES } from '@/js/lib/font'
import { FroalaBeforeCommand } from '@/js/components/common/froala-after-command'

import TextOptions from './TextOptions.vue'
import IconImageOptions from './IconImageOptions.vue'
import Background from './Background.vue'
import TemplateOptions from './TemplateOptions.vue'
import { LEFT_SIDEBAR_FRESHWORKS } from '../../../../../../../storybook/components/constants'
import Alignment from './Alignment.vue'
import GenAIOptions from '../../../genAi/GenAIOptions.vue'
import BorderStyleOptions from '../BorderStyleOptions.vue'

export default {
  components: {
    Alignment,
    GenAIOptions,
    TextOptions,
    IconImageOptions,
    Background,
    TemplateOptions,
    BorderStyleOptions,
  },
  props: {
    mode: {
      type: String,
      default: 'LIGHT',
    },
    prependChevron: {
      type: Boolean,
      default: false,
    },
    isCardElement: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      selected: getters.SELECTOR_SELECTED,
      config: getters.AUTH_GET_USER_CONFIG,
      viewMode: getters.VIEW_MODE,
      availableFonts: getters.THEME_AVAILABLE_FONTS,
      colors: getters.THEME_GET_COLORS,
      typography: getters.THEME_TYPOGRAPHY,
      currentProductVersionNumber: getters.CURRENT_PRODUCT_VERSION_NUMBER,
      themeManagerVersion: getters.THEME_MANAGER_VERSION,
    }),
    ...mapState('globalTheme', {
      OrgName: ({ globalTheme }) =>
        globalTheme.OrgName !== undefined ? globalTheme.OrgName : 'standard',
    }),
    isSpecialStyle() {
      return this.OrgName === LEFT_SIDEBAR_FRESHWORKS
    },
    genAiEnabled() {
      return (
        (this.config.IS_GEN_AI_ENABLED || false) &&
        this.currentProductVersionNumber >= 4.0
      )
    },
    enableButton: {
      get() {
        return _.get(this, 'selected.template.button.enable', false)
      },
      set(value) {
        return this.selected.updateProp('template/button/enable', value)
      },
    },
    enableBackground: {
      get() {
        return _.get(this, 'selected.template.enableBackground', true)
      },
    },
    enableHeadline: {
      get() {
        return _.get(this, 'selected.template.headline.enable', true)
      },
    },
    enableIcon: {
      get() {
        return _.get(this, 'selected.template.icon.enable', true)
      },
    },
    enableText: {
      get() {
        return _.get(this, 'selected.template.subHeadline.enable', true)
      },
    },
    headLineHTML: {
      get() {
        return _.get(this, 'selected.template.headline.text', 'Edit Me')
      },
      set(value) {
        this.selected.updateProp('template/headline/text', value)
      },
    },
    textHTML: {
      get() {
        return _.get(this, 'selected.template.subHeadline.text', 'Edit Me')
      },
      set(value) {
        this.selected.updateProp('template/subHeadline/text', value)
      },
    },
    headlineConfig() {
      return this.getFroalaConfig(
        [
          'customParagraphStyleOne',
          'textColor',
          'bold',
          'italic',
          'underline',
          'strikeThrough',
          'clearFormatting',
          'alignLeft',
          'alignCenter',
          'alignRight',
          'undo',
          'redo',
          'fontWeight',
          'backgroundColor',
        ],
        {
          saveParams: { id: 'my_editor_headlineConfig' },
        },
      )
    },
    textConfig() {
      return this.getFroalaConfig(
        [
          'customParagraphStyleTwo',
          'textColor',
          'bold',
          'italic',
          'underline',
          'strikeThrough',
          'clearFormatting',
          'alignLeft',
          'alignCenter',
          'alignRight',
          'undo',
          'redo',
          'fontWeight',
          'backgroundColor',
          'formatOLSimple',
          'customList',
        ],
        {
          saveParams: { id: 'my_editor_textConfig' },
        },
      )
    },
  },
  methods: {
    showOption(...views) {
      return views.includes(this.viewMode)
    },
    onSwitchToggle(property) {
      this[property] = !this[property]
    },
    getHexColors() {
      // Convert THEME_RGBA_COLOR to HEX format
      return Object.keys(this.colors).map((key) => {
        if (this.themeManagerVersion === 1) {
          let r = this.colors[key]['color'][0]
          let g = this.colors[key]['color'][1]
          let b = this.colors[key]['color'][2]
          return rgbToHex(r, g, b)
        } else {
          return `var(--${key})`
        }
      })
    },
    getFontFamily() {
      let fontFamily = {}
      this.availableFonts.forEach((font) => {
        fontFamily[`${font.familyName}, ${font.genericFamily}`] =
          font.familyName
      })
      return fontFamily
    },

    selectAction(value, computedProperty) {
      this[computedProperty] = value
    },
    getFroalaConfig(moreMiscButtons, otherConfig) {
      let hexColors = this.getHexColors()
      let fontFamily = this.getFontFamily()

      let styleFonts = {}
      let styleFontSize = {}

      Object.keys(this.typography).forEach((key) => {
        styleFonts[key] = this.typography[key]['font']
        styleFontSize[key] = this.typography[key]['size']
      })

      return {
        key: this.config?.FROALA_EDITOR_ID,
        toolbarBottom: true,
        toolbarStickyOffset: false,
        colorsText: hexColors,
        colorsBackground: hexColors,
        colorsHEXInput: this.themeManagerVersion === 1,
        fontFamilySelection: true,
        fontFamily: fontFamily,
        fontSize: FROALA_FONT_SIZES,
        initOnClick: false,
        keepFormatOnDelete: true,
        pastePlain: true,
        pasteDeniedTags: ['div'],
        pasteDeniedAttrs: ['class', 'style'],
        paragraphMultipleStyles: false,
        placeholderText: this.$t(
          'editor.section.grid_item.edit.placeholder_text',
        ),
        paragraphFormatSelection: false,
        charCounterMax: false,
        charCounterCount: false,
        toolbarButtons: {
          moreMisc: {
            buttons: moreMiscButtons,
            buttonsVisible: 20,
          },
        },
        ...otherConfig,
        events: {
          ...(otherConfig.events || {}),
          'commands.before': function (cmd, param1, param2) {
            // Handle CSS variable colors before Froala's default handling
            return FroalaBeforeCommand(this, cmd, param1, param2)
          },
        },
      }
    },
  },
}
</script>

<style lang="scss">
.pointer-events-none {
  pointer-events: none;
  opacity: 0.5;
}
</style>
